import { useId, useState } from 'react';

import {
  defaultCadence,
  everyPrefix,
  formatDate,
  unitLabel,
} from '@splitfiesta/core';
import { useTranslation } from '@splitfiesta/i18n';
import {
  MAX_RECURRING_EVERY,
  RECURRING_UNITS,
  type RecurringCadence,
  type RecurringUnit,
} from '@splitfiesta/shared';
import { Card, FormSwitch } from '@splitfiesta/ui';

import { getInputClasses } from '@/lib/styles/form';

interface ExpenseRepeatProps {
  cadence: RecurringCadence | null;
  onCadenceChange: (cadence: RecurringCadence | null) => void;
  endDate: string | null;
  onEndDateChange: (endDate: string | null) => void;
  preview: { nextDate: string; endsBefore: boolean } | null;
  disabled: boolean;
}

const LABEL_CLASSES =
  'block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1';
const CONTROL_CLASSES = 'min-h-11';

export function ExpenseRepeat({
  cadence,
  onCadenceChange,
  endDate,
  onEndDateChange,
  preview,
  disabled,
}: ExpenseRepeatProps) {
  const { t, i18n } = useTranslation();
  const id = useId();
  const ids = {
    toggle: `${id}-toggle`,
    everyLabel: `${id}-every-label`,
    every: `${id}-every`,
    unit: `${id}-unit`,
    end: `${id}-end`,
  };
  const shown = cadence ?? defaultCadence;
  const prefix = everyPrefix(shown.unit, shown.every);

  // The number field may be empty or out of range while typing; only a
  // valid count reaches the form.
  const [everyText, setEveryText] = useState(String(shown.every));
  const [syncedEvery, setSyncedEvery] = useState(shown.every);
  if (syncedEvery !== shown.every) {
    setSyncedEvery(shown.every);
    setEveryText(String(shown.every));
  }

  const onEveryChange = (value: string) => {
    setEveryText(value);
    const every = Number(value);
    if (Number.isInteger(every) && every >= 1 && every <= MAX_RECURRING_EVERY) {
      onCadenceChange({ ...shown, every });
    }
  };

  const formatDay = (value: string) => formatDate(value, i18n.language);

  return (
    <Card>
      <div data-testid="expense-repeat" className="space-y-4">
        <FormSwitch
          id={ids.toggle}
          label={t('expenses:repeat.label')}
          checked={!!cadence}
          onChange={on => onCadenceChange(on ? shown : null)}
          disabled={disabled}
          data-testid="expense-repeat-switch"
        />

        {cadence && (
          <>
            <div
              data-testid="expense-repeat-sentence"
              className="flex flex-wrap items-center gap-x-3 gap-y-2"
            >
              <label
                id={ids.everyLabel}
                htmlFor={ids.every}
                className="text-lg font-semibold text-gray-900 dark:text-gray-50"
              >
                {t(prefix.key, { count: prefix.count })}
              </label>
              <div className="w-24">
                <input
                  id={ids.every}
                  type="number"
                  inputMode="numeric"
                  min={1}
                  max={MAX_RECURRING_EVERY}
                  step={1}
                  data-testid="expense-repeat-every"
                  value={everyText}
                  onChange={e => onEveryChange(e.target.value)}
                  onBlur={() => setEveryText(String(cadence.every))}
                  disabled={disabled}
                  className={getInputClasses({
                    disabled,
                    className: CONTROL_CLASSES,
                  })}
                />
              </div>
              <div className="min-w-[45%] flex-1 sm:min-w-0 sm:flex-none sm:w-40">
                <select
                  id={ids.unit}
                  aria-labelledby={`${ids.everyLabel} ${ids.every}`}
                  data-testid="expense-repeat-unit"
                  value={cadence.unit}
                  onChange={e =>
                    onCadenceChange({
                      ...cadence,
                      unit: e.target.value as RecurringUnit,
                    })
                  }
                  disabled={disabled}
                  className={getInputClasses({
                    disabled,
                    className: CONTROL_CLASSES,
                  })}
                >
                  {RECURRING_UNITS.map(unit => {
                    const label = unitLabel(unit, cadence.every);
                    return (
                      <option key={unit} value={unit}>
                        {t(label.key, { count: label.count })}
                      </option>
                    );
                  })}
                </select>
              </div>
            </div>

            {preview && (
              <p
                data-testid="expense-repeat-preview"
                aria-live="polite"
                className="text-base font-medium text-gray-600 dark:text-gray-400"
              >
                {endDate
                  ? t('expenses:repeat.nextWithEnd', {
                      date: formatDay(preview.nextDate),
                      endDate: formatDay(endDate),
                    })
                  : t('expenses:repeat.next', {
                      date: formatDay(preview.nextDate),
                    })}
              </p>
            )}
            <div className="sm:w-1/2">
              <label htmlFor={ids.end} className={LABEL_CLASSES}>
                {t('expenses:repeat.ends')}
              </label>
              <input
                id={ids.end}
                type="date"
                data-testid="expense-repeat-end"
                value={endDate ?? ''}
                min={preview?.nextDate}
                onChange={e => onEndDateChange(e.target.value || null)}
                disabled={disabled}
                className={getInputClasses({
                  disabled,
                  className: CONTROL_CLASSES,
                })}
              />
            </div>

            {preview?.endsBefore && (
              <p
                data-testid="expense-repeat-ends-before"
                role="status"
                aria-live="polite"
                className="text-sm text-amber-700 dark:text-amber-400"
              >
                {t('expenses:repeat.endsBefore')}
              </p>
            )}
          </>
        )}
      </div>
    </Card>
  );
}

export default ExpenseRepeat;
